import { useEffect, useRef, useState, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";

import "./stylesUserCard.css";

import BronzeIcon from "../../../../assets/icons/ic_bronze-status.svg";
import VerifiedIcon from "../../../../assets/icons/ic_verified.svg";

import {
  Pause,
  UserX,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "react-feather";
import { Modal, Avatar, Skeleton, Tooltip } from "antd";
import SuspendModal from "./SuspendModal/SuspendModal";
import UserDeletionModal from "./UserDeletionModal/UserDeletionModal";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  BRAND_ICONS_URLS,
  CASINO_PROVIDERS_INNER_FLAGS,
  SPORTSBOOK_PROVIDERS_INNER_FLAGS,
  AGENT_ACTIONS,
} from "../../../../utils/Constants";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import {
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import GlobalChatModal from "./GlobalChatModal/GlobalChatModal";
import GamePassModal from "./GamePassModal/GamePassModal";
import VerificationEmailModal from "./VerificationEmailModal/VerificationEmailModal";
import AvailablePromotionsModal from "./AvailablePromotionsModal/AvailablePromotionsModal";
import ProfitBoostModal from "./ProfitBoostModal/ProfitBoostModal";
import PaymentsInstrumentsModal from "./PaymentsInstrumentsModal/PaymentsInstrumentsModal";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import FreeBetModal from "./FreeBetModal/FreeBetModal";
import UserBalanceModal from "./UserBalanceModal/UserBalanceModal";
import EditUserDataModal from "./EditUserDataModal/EditUserDataModal";
import AppStoreWrittenReviewModal from "./AppStoreWrittenReviewModal/AppStoreWrittenReviewModal";
import PersonalLimitsPopup from "./PersonalLimitsPopup/PersonalLimitsPopup";
import FreeSpinsModal from "./FreeSpinsModal/FreeSpinsModal";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import FeatureFlagService from "../../../../services/FeatureFlagService";
import AppContext from "../../../../context/AppContext";
import SportsbookVIPModal from "./SportsbookVIPModal/SportsbookVIPModal";
import SeonCooldownModal from "./SeonCooldownModal/SeonCooldownModal";
import { useGetBetLimitsTier } from "../../../../queries/user-card/useGetBetLimitsTier";
import {
  SPORTSBOOK_TIERS,
  normalizeSportsbookTier,
} from "../../../../utils/constants/sportsbookTiers";
import SportsbookIcon from "../../../../assets/icons/ic_sportsbook.svg";
import { isTruthyFlag } from "../../../../utils/UserUtils";
import { isGamePassFeatureEnabled } from "../../../../utils/gamePassFeature";
import { buildUsersFiltersUrl } from "../../../../utils/NavigationUtils";
import { formatCurrency } from "../../../../utils/financialFormatters";

// Sportsbook provider feature flags
const SPORTSBOOK_PROVIDERS = ["dst", "oddin", "onefeed", "sportsradar"];
// Casino provider feature flags
const CASINO_PROVIDERS = [
  "threeoaks",
  "spribe",
  "evolution",
  "booming",
  "saGaming",
  "3oaks",
  "jacktop",
  "pragmatic",
  "goldenrace",
  "betcore",
  "royal",
  "bgaming",
  "iconic21",
  "kerma",
];

function UserCard({
  data,
  refreshUserData,
  isLoading,
  currencySymbol,
  setIsLoading,
  openModal,
}) {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );
  const { adminActions } = useContext(AppContext);

  const canEditWallet = adminActions?.includes(AGENT_ACTIONS.EDIT_USER_WALLET);
  const canDeleteUsers = adminActions?.includes(
    AGENT_ACTIONS.SUSPEND_DISABLE_PAM_USER,
  );
  const canSuspendUsers = adminActions?.includes(AGENT_ACTIONS.SUSPEND_USER);
  const canAddLimits = adminActions?.includes(AGENT_ACTIONS.ADD_LIMITS);

  const availablePromotionsModalRef = useRef(null);
  const profitBoostModalRef = useRef(null);
  const paymentInstrumentsModalRef = useRef(null);
  const suspendModalRef = useRef(null);
  const userDeletionModalRef = useRef(null);
  const freeBetModalRef = useRef(null);
  const userBalanceModalRef = useRef(null);
  const editUserDataModalRef = useRef(null);
  const appStoreWrittenReviewModalRef = useRef(null);
  const freeSpinsModalRef = useRef(null);
  const { appConfig } = useSelector((state) => state.config);
  const isSweepSTakesClient =
    appConfig?.app_currency_config?.application_type === "sweepstakes";

  // Case-insensitive: PAM_APP_NAME comes from remotely served TCM strings,
  // so an exact-case match silently hides the Pikkem balances on value drift.
  const isPikkemClient = ["rebet", "test03 pam"].includes(
    (AppStrings?.PAM_APP_NAME ?? "").toLowerCase(),
  );
  // const isPikkemClient =
  //   appConfig?.features_status_config?.additional_features?.pikkem;

  const hasSocialFeeds =
    appConfig?.features_status_config?.social_features?.feeds;

  const isSeonTriggered2faEnabled =
    appConfig?.features_status_config?.["2fa"]?.seon_triggered ?? false;

  const {
    username,
    first_name,
    last_name,
    rank,
    kyc_status,
    num_of_followers,
    referral_code,
    referral_code_own,
    referral_count,
    num_of_duplicate_bets,
    rebet_cash,
    primary_currency_balance,
    rebet_coin,
    secondary_currency_balance,
    not_wagered_rebet_cash,
    primary_not_wagered_currency_balance,
    casino_cash,
    p_cash = 0,
    not_wagered_p_cash = 0,
    prediction_cash = 0,
    not_wagered_prediction_cash = 0,
    profile_photo,
    is_deleted,
  } = data;
  const referredUsersUrl = buildUsersFiltersUrl({
    referral_code: referral_code_own,
  });

  const [isSuspendUserModalOpen, setIsSuspendUserModalOpen] = useState(false);
  const [isUserDeletionModalOpen, setIsUserDeletionModalOpen] = useState(false);
  const [isGlobalChatModalOpen, setIsGlobalChatModalOpen] = useState(false);
  const [isGamePassModalOpen, setIsGamePassModalOpen] = useState(false);
  const [isVerificationEmailModalOpen, setIsVerificationEmailModalOpen] =
    useState(false);
  const [isAvailablePromotionsModalOpen, setIsAvailablePromotionsModalOpen] =
    useState(false);
  const [isProfitBoostModalOpen, setIsProfitBoostModalOpen] = useState(false);
  const [isPaymentInstrumentsPopupOpen, setIsPaymentInstrumentsPopupOpen] =
    useState(false);
  const [isFreeBetModalOpen, setIsFreeBetModalOpen] = useState(false);
  const [isUserBalanceModalOpen, setIsUserBalanceModalOpen] = useState(false);
  const [isEditUserDataModalOpen, setIsEditUserDataModalOpen] = useState(false);
  const [
    isAppStoreWrittenReviewModalOpen,
    setIsAppStoreWrittenReviewModalOpen,
  ] = useState(false);
  const [isPersonalLimitsModalOpen, setIsPersonalLimitsModalOpen] =
    useState(false);
  const [isFreeSpinsModalOpen, setIsFreeSpinsModalOpen] = useState(false);
  const [hasSportsbookProvider, setHasSportsbookProvider] = useState(false);
  const [hasCasinoProvider, setHasCasinoProvider] = useState(false);
  const [isPredictionsClient, setIsPredictionsClient] = useState(false);
  const gamePassPlatformEnabled = useSelector((state) =>
    isGamePassFeatureEnabled(state.config?.appConfig?.features_status_config),
  );
  const [isGamePassServiceEnabled, setIsGamePassServiceEnabled] =
    useState(false);
  const isGamePassEnabled =
    gamePassPlatformEnabled || isGamePassServiceEnabled;
  const [isSportsbookVIPModalOpen, setIsSportsbookVIPModalOpen] =
    useState(false);
  const [isMoreActionsExpanded, setIsMoreActionsExpanded] = useState(false);
  const [isSeonCooldownModalOpen, setIsSeonCooldownModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const { data: betLimitsTierData } = useGetBetLimitsTier(data?.user_id);
  const sportsbookTier = normalizeSportsbookTier(betLimitsTierData?.tier);
  const isVip = sportsbookTier === SPORTSBOOK_TIERS.VIP;
  const isAffiliate = sportsbookTier === SPORTSBOOK_TIERS.AFFILIATE;

  // Check feature flags for sportsbook and casino providers
  useEffect(() => {
    const checkProviders = async () => {
      await FeatureFlagService.initialize();
      const hasSportsbook = SPORTSBOOK_PROVIDERS.some((provider) =>
        FeatureFlagService.isEnabled(provider),
      );
      const hasCasino = CASINO_PROVIDERS.some((provider) =>
        FeatureFlagService.isEnabled(provider),
      );
      setHasSportsbookProvider(hasSportsbook);
      setHasCasinoProvider(hasCasino);
      // Catalog-only: predictions was never flag-gated, so `isProductActive`
      // would fail-open in non-dev (PAMENG-1820). Presence in v2 = enabled.
      setIsPredictionsClient(
        FeatureFlagService.isProductKeyActive(TRIVELTA_PRODUCTS.PREDICTIONS),
      );
      // TODO(PAMENG-1820): drop the "gamePass" fallback arg after rollout.
      setIsGamePassServiceEnabled(
        FeatureFlagService.isProductActive(
          TRIVELTA_PRODUCTS.GAME_PASS,
          "gamePass",
        ),
      );
    };
    checkProviders();
  }, []);

  const showSuspendUserModal = () => {
    setIsSuspendUserModalOpen(true);
  };
  const [transactions, setTransactions] = useState({
    total_purchases: 0,
    total_withdrawals: 0,
    lifetime_value: 0,
  });

  const handleSuspendUserModalOk = () => {
    setIsSuspendUserModalOpen(false);
  };

  const handleSuspendUserModalCancel = () => {
    setIsSuspendUserModalOpen(false);
  };

  const handleUserDeletionModalOk = () => {
    setIsUserDeletionModalOpen(false);
  };

  const handleUserDeletionModalCancel = () => {
    setIsUserDeletionModalOpen(false);
  };

  const handleGlobalChatModalOk = () => {
    setIsGlobalChatModalOpen(false);
  };

  const handleGlobalChatModalCancel = () => {
    setIsGlobalChatModalOpen(false);
  };

  const showGlobalChatModal = () => {
    setIsGlobalChatModalOpen(true);
  };

  const showUserDeletionModal = () => {
    setIsUserDeletionModalOpen(true);
  };

  const handleGamePassModalOk = () => {
    setIsGamePassModalOpen(false);
  };

  const handleGamePassModalCancel = () => {
    setIsGamePassModalOpen(false);
  };

  const showGamePassModal = () => {
    setIsGamePassModalOpen(true);
  };

  const showSportsbookVIPModal = () => {
    setIsSportsbookVIPModalOpen(true);
  };

  const handleSportsbookVIPModalCancel = () => {
    setIsSportsbookVIPModalOpen(false);
  };

  const handleSportsbookVIPSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["betLimitsTier", data?.user_id],
    });
    setIsSportsbookVIPModalOpen(false);
  };

  const handleVerificationEmailModalOk = () => {
    setIsVerificationEmailModalOpen(false);
  };

  const handleVerificationEmailModalCancel = () => {
    setIsVerificationEmailModalOpen(false);
  };

  const showVerificationEmailModal = () => {
    setIsVerificationEmailModalOpen(true);
  };

  const showSeonCooldownModal = () => {
    setIsSeonCooldownModalOpen(true);
  };

  const handleSeonCooldownModalCancel = () => {
    setIsSeonCooldownModalOpen(false);
  };

  const handleAvailablePromotionsModalOk = () => {
    setIsAvailablePromotionsModalOpen(false);
  };

  const handleAvailablePromotionsModalCancel = () => {
    setIsAvailablePromotionsModalOpen(false);
  };

  const showAvailablePromotionsModal = () => {
    setIsAvailablePromotionsModalOpen(true);
  };

  const handleProfitBoostModalOk = () => {
    setIsProfitBoostModalOpen(false);
  };

  const handleProfitBoostModalCancel = () => {
    setIsProfitBoostModalOpen(false);
  };

  const showProfitBoostModal = () => {
    setIsProfitBoostModalOpen(true);
  };

  const handlePaymentInstrumentsPopupClose = () => {
    setIsPaymentInstrumentsPopupOpen(false);
  };

  const handlePaymentInstrumentsPopupOk = () => {
    setIsPaymentInstrumentsPopupOpen(false);
  };

  const showPaymentInstrumentsPopup = () => {
    setIsPaymentInstrumentsPopupOpen(true);
  };

  const handleFreeBetModalOk = () => {
    setIsFreeBetModalOpen(false);
  };

  const handleFreeBetModalCancel = () => {
    setIsFreeBetModalOpen(false);
  };

  const showFreeBetModal = () => {
    setIsFreeBetModalOpen(true);
  };

  const handleUserBalanceModalOk = () => {
    setIsUserBalanceModalOpen(false);
  };

  const handleUserBalanceModalCancel = () => {
    setIsUserBalanceModalOpen(false);
  };

  const showUserBalanceModal = () => {
    setIsUserBalanceModalOpen(true);
  };

  const handleEditUserDataModalOk = () => {
    setIsEditUserDataModalOpen(false);
  };

  const handleEditUserDataModalCancel = () => {
    setIsEditUserDataModalOpen(false);
  };

  const showEditUserDataModal = () => {
    setIsEditUserDataModalOpen(true);
  };

  const handleAppStoreWrittenReviewModalOk = () => {
    setIsAppStoreWrittenReviewModalOpen(false);
  };

  const handleAppStoreWrittenReviewModalCancel = () => {
    setIsAppStoreWrittenReviewModalOpen(false);
  };

  const showAppStoreWrittenReviewModal = () => {
    setIsAppStoreWrittenReviewModalOpen(true);
  };

  const handlePersonalLimitsModalOk = () => {
    setIsPersonalLimitsModalOpen(false);
  };

  const handlePersonalLimitsModalCancel = () => {
    setIsPersonalLimitsModalOpen(false);
  };

  const showPersonalLimitsModal = () => {
    setIsPersonalLimitsModalOpen(true);
  };

  const showFreeSpinsModal = () => {
    setIsFreeSpinsModalOpen(true);
  };
  const handleFreeSpinsModalOk = () => {
    setIsFreeSpinsModalOpen(false);
  };
  const handleFreeSpinsModalCancel = () => {
    setIsFreeSpinsModalOpen(false);
  };

  const handleBadgeStatus = (data) => {
    if (isTruthyFlag(is_deleted)) {
      return AppStrings.DELETED;
    }
    if (isTruthyFlag(data?.suspended)) {
      return AppStrings.SUSPENDED;
    }
    return AppStrings.ACTIVE;
  };

  const handleColorBasedOnValue = (value, isLtv = false) => {
    if (value > 0 && !isLtv) {
      return "orange";
    }
    if (value > 0 && isLtv) {
      return "green";
    }
    if (value === 0) {
      return "white";
    }
    return "red";
  };

  const getTransactions = async () => {
    setIsLoading(true);
    if (!data?.user_id) return;

    try {
      const response = await PrivateAdminPanelInstance?.get(
        API_ROUTES?.GET_TRANSACTION_HISTORY?.replace(
          "{user_id}",
          data?.user_id,
        ),
      );
      const response_data = response?.data?.data;
      setTransactions({
        total_purchases: response_data?.totals?.total_purchased || 0,
        total_withdrawals: response_data?.totals?.total_withdrawn || 0,
        lifetime_value:
          (response_data?.totals?.total_purchased || 0) -
          (response_data?.totals?.total_withdrawn || 0),
      });
    } catch (error) {
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    }
  };

  useEffect(() => {
    getTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.user_id]);

  // Open modal based on URL parameter
  useEffect(() => {
    if (openModal && data?.user_id && !isLoading) {
      if (openModal === "user_wallet") {
        setIsUserBalanceModalOpen(true);
      } else if (openModal === "delete_user") {
        setIsUserDeletionModalOpen(true);
      }
    }
  }, [openModal, data?.user_id, isLoading]);

  return (
    <div className="user-card-container">
      <Modal
        closable={false}
        open={isSuspendUserModalOpen}
        onOk={handleSuspendUserModalOk}
        onCancel={handleSuspendUserModalCancel}
        footer={null}
        width={500}
        afterClose={() => {
          suspendModalRef.current?.clearFormData();
        }}
      >
        <SuspendModal
          ref={suspendModalRef}
          handleCancel={handleSuspendUserModalCancel}
          data={data}
          refreshUserData={refreshUserData}
        />
      </Modal>

      <Modal
        closable={false}
        open={isUserDeletionModalOpen}
        onOk={handleUserDeletionModalOk}
        onCancel={handleUserDeletionModalCancel}
        footer={null}
        width={500}
        afterClose={() => {
          userDeletionModalRef.current?.clearFormData();
        }}
      >
        <UserDeletionModal
          ref={userDeletionModalRef}
          handleCancel={handleUserDeletionModalCancel}
          userData={data}
          refreshUserData={refreshUserData}
        />
      </Modal>

      <Modal
        closable={false}
        open={isGlobalChatModalOpen}
        onOk={handleGlobalChatModalOk}
        onCancel={handleGlobalChatModalCancel}
        footer={null}
        width={500}
      >
        <GlobalChatModal
          handleCancel={handleGlobalChatModalCancel}
          data={data}
          refreshUserData={refreshUserData}
        />
      </Modal>

      <Modal
        closable={false}
        open={isGamePassModalOpen}
        onOk={handleGamePassModalOk}
        onCancel={handleGamePassModalCancel}
        footer={null}
        width={500}
      >
        <GamePassModal
          handleCancel={handleGamePassModalCancel}
          data={data}
          refreshUserData={refreshUserData}
        />
      </Modal>

      <Modal
        closable={false}
        open={isSportsbookVIPModalOpen}
        onCancel={handleSportsbookVIPModalCancel}
        footer={null}
        width={500}
      >
        <SportsbookVIPModal
          handleCancel={handleSportsbookVIPModalCancel}
          onSuccess={handleSportsbookVIPSuccess}
          data={data}
          currentTier={sportsbookTier}
        />
      </Modal>

      <Modal
        closable={false}
        open={isVerificationEmailModalOpen}
        onOk={handleVerificationEmailModalOk}
        onCancel={handleVerificationEmailModalCancel}
        footer={null}
        width={500}
      >
        <VerificationEmailModal
          handleCancel={handleVerificationEmailModalCancel}
          data={data}
        />
      </Modal>

      <Modal
        closable={false}
        open={isSeonCooldownModalOpen}
        onCancel={handleSeonCooldownModalCancel}
        footer={null}
        width={500}
      >
        <SeonCooldownModal
          handleCancel={handleSeonCooldownModalCancel}
          data={data}
        />
      </Modal>

      <Modal
        closable={false}
        open={isAvailablePromotionsModalOpen}
        onOk={handleAvailablePromotionsModalOk}
        onCancel={handleAvailablePromotionsModalCancel}
        footer={null}
        width={500}
        afterOpenChange={(open) => {
          if (open) {
            availablePromotionsModalRef.current?.getAvailablePromotions();
          }
        }}
        afterClose={() => {
          availablePromotionsModalRef.current?.resetDeleteFlow();
        }}
      >
        <AvailablePromotionsModal
          disabled={!canEditWallet}
          ref={availablePromotionsModalRef}
          handleCancel={handleAvailablePromotionsModalCancel}
          data={data}
        />
      </Modal>

      <Modal
        closable={false}
        open={isProfitBoostModalOpen}
        onOk={handleProfitBoostModalOk}
        onCancel={handleProfitBoostModalCancel}
        footer={null}
        width={500}
        afterClose={() => {
          profitBoostModalRef.current?.clearForm();
        }}
      >
        <ProfitBoostModal
          ref={profitBoostModalRef}
          handleCancel={handleProfitBoostModalCancel}
          data={data}
        />
      </Modal>

      <Modal
        closable={false}
        open={isPaymentInstrumentsPopupOpen}
        onOk={handlePaymentInstrumentsPopupOk}
        onCancel={handlePaymentInstrumentsPopupClose}
        footer={null}
        width={500}
        afterOpenChange={(open) => {
          if (open) {
            paymentInstrumentsModalRef.current?.fetchAllBanksOfAllTypes();
          }
        }}
        afterClose={() => {
          paymentInstrumentsModalRef.current?.handleClose();
        }}
      >
        <PaymentsInstrumentsModal
          ref={paymentInstrumentsModalRef}
          handleCancel={handlePaymentInstrumentsPopupClose}
          data={data}
        />
      </Modal>

      <Modal
        closable={false}
        open={isFreeBetModalOpen}
        onOk={handleFreeBetModalOk}
        onCancel={handleFreeBetModalCancel}
        footer={null}
        width={500}
        afterClose={() => {
          freeBetModalRef.current?.clearForm();
        }}
      >
        <FreeBetModal
          handleCancel={handleFreeBetModalCancel}
          data={data}
          ref={freeBetModalRef}
        />
      </Modal>

      <Modal
        closable={false}
        open={isFreeSpinsModalOpen}
        onOk={handleFreeSpinsModalOk}
        onCancel={handleFreeSpinsModalCancel}
        footer={null}
        width={500}
        afterClose={() => {
          freeSpinsModalRef.current?.clearForm();
        }}
      >
        <FreeSpinsModal
          handleCancel={handleFreeSpinsModalCancel}
          data={data}
          ref={freeSpinsModalRef}
        />
      </Modal>
      <Modal
        closable={false}
        open={isUserBalanceModalOpen}
        onOk={handleUserBalanceModalOk}
        onCancel={handleUserBalanceModalCancel}
        footer={null}
        width={800}
        afterClose={() => {
          userBalanceModalRef.current?.clearFormData();
        }}
      >
        <UserBalanceModal
          ref={userBalanceModalRef}
          handleCancel={handleUserBalanceModalCancel}
          refreshUserData={refreshUserData}
          data={data}
          isSweepSTakesClient={isSweepSTakesClient}
          hasCasinoProvider={hasCasinoProvider}
          isPikkemClient={isPikkemClient}
          isPredictionsClient={isPredictionsClient}
        />
      </Modal>

      <Modal
        closable={false}
        open={isEditUserDataModalOpen}
        onOk={handleEditUserDataModalOk}
        onCancel={handleEditUserDataModalCancel}
        footer={null}
        width={700}
        afterClose={() => {
          editUserDataModalRef.current?.clearFormData();
        }}
      >
        <EditUserDataModal
          ref={editUserDataModalRef}
          handleCancel={handleEditUserDataModalCancel}
          data={data}
          refreshUserData={refreshUserData}
        />
      </Modal>

      <Modal
        closable={false}
        open={isAppStoreWrittenReviewModalOpen}
        onOk={handleAppStoreWrittenReviewModalOk}
        onCancel={handleAppStoreWrittenReviewModalCancel}
        footer={null}
        width={500}
      >
        <AppStoreWrittenReviewModal
          refreshUserData={refreshUserData}
          ref={appStoreWrittenReviewModalRef}
          handleCancel={handleAppStoreWrittenReviewModalCancel}
          data={data}
        />
      </Modal>
      <Modal
        closable={false}
        open={isPersonalLimitsModalOpen}
        onOk={handlePersonalLimitsModalOk}
        onCancel={handlePersonalLimitsModalCancel}
        footer={null}
        width={{
          xs: "90%",
          sm: "90%",
          md: "90%",
          lg: 1200,
        }}
      >
        <PersonalLimitsPopup
          isVisible={isPersonalLimitsModalOpen}
          onClosePressed={handlePersonalLimitsModalCancel}
          userId={data?.user_id}
          hasSportsbookProvider={hasSportsbookProvider}
          hasCasinoProvider={hasCasinoProvider}
          initialUserLimitsLocked={data?.is_user_locked_limit}
          initialIsPreventFromWithdraw={data?.is_prevent_from_withdraw}
          initialBonusBlocked={data?.bonus_enabled === false}
        />
      </Modal>
      {isLoading ? (
        <Skeleton avatar loading={true} paragraph={{ rows: 5 }} active />
      ) : (
        <>
          {(isVip || isAffiliate) && (
            <div className="vip-badge">
              <img
                src={
                  ["test03", "rebet"].some((value) =>
                    (AppStrings?.PAM_APP_NAME?.toLowerCase() ?? "").includes(value),
                  )
                    ? BronzeIcon
                    : `${process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL}${BRAND_ICONS_URLS.topLeftAppIcon}`
                }
                alt={isAffiliate ? "affiliate icon" : "vip icon"}
              />
              <span>
                {(isAffiliate
                  ? AppStrings.SPORTSBOOK_AFFILIATE
                  : AppStrings.SPORTSBOOK_VIP
                )?.toUpperCase()}
              </span>
            </div>
          )}
          <div className="user-card-header">
            <div className="bagde-container">
              <div className="bagde-bettor">
                <img src={BronzeIcon} alt="bronze icon" />
                {rank || "N/A"}
              </div>
              <div className="avatar-container">
                <Avatar
                  size={50}
                  src={profile_photo}
                  style={{
                    backgroundColor: "var(--COLOR-PRIMARY)",
                    flexShrink: 0,
                    fontSize: 30,
                  }}
                >
                  {username?.charAt(0)?.toUpperCase()}
                </Avatar>
              </div>
              <div
                className={`bagde ${handleBadgeStatus(data)?.toLowerCase()}`}
              >
                {handleBadgeStatus(data)}
              </div>
            </div>
            <div className="username-container">
              <div className="name-container">
                <p className="name">
                  {first_name} {last_name}
                </p>
                {kyc_status === "verified" ? (
                  <div className="kyc-status">
                    <img src={VerifiedIcon} alt="verified icon" />
                  </div>
                ) : (
                  <div className="kyc-status">
                    <ClockCircleOutlined className="kyc-status-icon-pending" />
                  </div>
                )}
              </div>
              <div className="num-of-followers-container">
                <p className="username">{username}</p>
                {hasSocialFeeds && (
                  <>
                    <span className="separator">|</span>
                    <p className="num-of-followers">
                      {num_of_followers} {AppStrings.FOLLOWERS}
                    </p>
                  </>
                )}
              </div>
              <button
                className={`edit-icon-button edit-user-data-icon-button ${!canEditWallet ? "disabled" : ""}`}
                onClick={showEditUserDataModal}
                disabled={!canEditWallet}
              >
                <EditOutlined />
              </button>
            </div>
          </div>
          <div className="indices">
            <div className="total-purchases border-right">
              <p>
                {AppStrings.TOTAL} <br /> {AppStrings.PURCHASES}
              </p>
              <span
                className={handleColorBasedOnValue(
                  transactions?.total_purchases,
                  false,
                )}
              >
                {formatCurrency(
                  Number(transactions?.total_purchases ?? 0),
                  currencySymbol,
                  2,
                )}
              </span>
            </div>
            <div className="total-prize-redemptions border-right">
              <p>
                {AppStrings.TOTAL} <br />
                {AppStrings.WITHDRAWALS}
              </p>
              <span
                className={handleColorBasedOnValue(
                  transactions?.total_withdrawals,
                  false,
                )}
              >
                {formatCurrency(
                  Number(transactions?.total_withdrawals ?? 0),
                  currencySymbol,
                  2,
                )}
              </span>
            </div>
            <div className="lifetime-value">
              <p>
                {AppStrings.LIFETIME} <br /> {AppStrings.VALUE}
              </p>
              <span
                className={handleColorBasedOnValue(
                  transactions?.lifetime_value,
                  true,
                )}
              >
                {formatCurrency(
                  Math.abs(Number(transactions?.lifetime_value ?? 0)),
                  currencySymbol,
                  2,
                )}
              </span>
            </div>
          </div>
          <div className="referral-code-container">
            <div className="referral-code own">
              <p className="placeholder">{AppStrings.REFERRAL_CODE_OWN}</p>
              <p className="code">{referral_code_own}</p>
            </div>
            <div className="referral-code members-referred">
              <div className="referral-label-row">
                <p className="placeholder">
                  {AppStrings.PROFILE_USERS_REFERRED}
                </p>
                {referral_code_own ? (
                  <Tooltip title={AppStrings.PROFILE_VIEW_REFERRED_USERS}>
                    <a
                      className="referred-users-link"
                      href={referredUsersUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={AppStrings.PROFILE_VIEW_REFERRED_USERS}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </Tooltip>
                ) : null}
              </div>
              <p className="code">{referral_count || 0}</p>
            </div>
          </div>
          <div className="referral-code-container">
            <div className="referral-code used">
              <p className="placeholder">{AppStrings.REFERRAL_CODE_USED}</p>
              <p className="code">{referral_code}</p>
            </div>
            {hasSportsbookProvider && (
              <div className="referral-code num-of-duplicate-bets">
                <p className="placeholder">
                  {AppStrings.NUM_OF_DUPLICATE_BETS}
                </p>
                <p className="code">{num_of_duplicate_bets || 0}</p>
              </div>
            )}
          </div>
          <div className="buttons">
            <button
              className={`button-suspend ${!canSuspendUsers ? "disabled" : ""}`}
              onClick={showSuspendUserModal}
              disabled={!canSuspendUsers}
            >
              <Pause size={16} />
              {isTruthyFlag(data?.suspended)
                ? AppStrings.UNSUSPEND
                : AppStrings.SUSPEND}
            </button>
            <button
              className={`button-delete ${!canDeleteUsers ? "disabled" : ""}`}
              onClick={showUserDeletionModal}
              disabled={!canDeleteUsers}
            >
              <UserX size={16} />
              {AppStrings.DELETE}
            </button>
          </div>

          <div className="user-balance">
            <div className="top">
              <p>{AppStrings.USER_BALANCE}</p>
              <button
                className={`edit-icon-button ${!canEditWallet ? "disabled" : ""}`}
                onClick={showUserBalanceModal}
                disabled={!canEditWallet}
              >
                <EditOutlined />
              </button>
            </div>
            <div className="rebet-cash-container">
              <div className="balance rebet-cash">
                <div>
                  <img
                    src={
                      process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                      BRAND_ICONS_URLS.primaryCurrencySymbol
                    }
                    alt="cash icon"
                  />
                  <p>{AppStrings.PRIMARY_CURRENCY}</p>
                </div>
                <span>
                  {Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(rebet_cash || primary_currency_balance) || 0}
                </span>
              </div>
              {isSweepSTakesClient && (
                <div className="balance rebet-coins">
                  <div>
                    <img
                      src={
                        process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                        BRAND_ICONS_URLS.secondaryCurrencySymbol
                      }
                      alt="coin icon"
                    />
                    <p>{AppStrings.SECONDARY_CURRENCY}</p>
                  </div>
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(rebet_coin || secondary_currency_balance) || 0}
                  </span>
                </div>
              )}
              <div className="balance rebet-cash redeemable-balance">
                <div>
                  <img
                    src={
                      process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                      BRAND_ICONS_URLS.primaryCurrencySymbol
                    }
                    alt="cash icon"
                  />
                  <p>{AppStrings.REDEEMABLE_CASH}</p>
                </div>
                <span>
                  {Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(
                    (rebet_cash || primary_currency_balance) -
                      (not_wagered_rebet_cash ||
                        primary_not_wagered_currency_balance),
                  ) || 0}
                </span>
              </div>
              {hasCasinoProvider && (
                <div className="balance casino-cash">
                  <div>
                    <img
                      src={
                        process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                        BRAND_ICONS_URLS.primaryCurrencySymbol
                      }
                      alt="casino cash icon"
                    />
                    <p>{AppStrings.CASINO_CASH}</p>
                  </div>
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(casino_cash) || 0}
                  </span>
                </div>
              )}
              {isPikkemClient && (
                <>
                  <div className="balance pikkem">
                    <div>
                      <DollarOutlined
                        style={{ color: "var(--COLOR-PRIMARY)" }}
                      />
                      <p>{AppStrings.PIKKEM}</p>
                    </div>
                    <span>
                      {Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(p_cash) || 0}
                    </span>
                  </div>
                  <div className="balance pikkem">
                    <div>
                      <DollarOutlined
                        style={{ color: "var(--COLOR-PRIMARY)" }}
                      />
                      <p>{AppStrings.PIKKEM_REDEEMABLE}</p>
                    </div>
                    <span>
                      {Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(p_cash - not_wagered_p_cash) || 0}
                    </span>
                  </div>
                </>
              )}
              {isPredictionsClient && (
                <>
                  <div className="balance prediction">
                    <div>
                      <DollarOutlined
                        style={{ color: "var(--COLOR-PRIMARY)" }}
                      />
                      <p>{AppStrings.PREDICTION_CASH}</p>
                    </div>
                    <span>
                      {Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(prediction_cash) || 0}
                    </span>
                  </div>
                  <div className="balance prediction">
                    <div>
                      <DollarOutlined
                        style={{ color: "var(--COLOR-PRIMARY)" }}
                      />
                      <p>{AppStrings.PREDICTION_REDEEMABLE}</p>
                    </div>
                    <span>
                      {Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(
                        prediction_cash - not_wagered_prediction_cash,
                      ) || 0}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="action-buttons payment-instruments">
              <button
                onClick={showPaymentInstrumentsPopup}
                disabled={!canEditWallet}
                className={`${!canEditWallet ? "disabled" : ""}`}
              >
                {AppStrings.EDIT_PAYMENT_INSTRUMENTS}
              </button>
            </div>

            <div className="action-buttons">
              <div className="top">
                <p>{AppStrings.PROMO}</p>
              </div>
              <button
                onClick={showAvailablePromotionsModal}
                disabled={!canEditWallet}
                className={`${!canEditWallet ? "disabled" : ""}`}
              >
                {AppStrings.AVAILABLE_PROMOTIONS}
              </button>
              <FeatureFlagGuard
                mode="conditional"
                innerFeatureFlags={SPORTSBOOK_PROVIDERS_INNER_FLAGS}
              >
                <button
                  onClick={showFreeBetModal}
                  disabled={!canEditWallet}
                  className={`${!canEditWallet ? "disabled" : ""}`}
                >
                  {AppStrings.FREE_BET}
                </button>
              </FeatureFlagGuard>
              <FeatureFlagGuard
                innerFeatureFlags={CASINO_PROVIDERS_INNER_FLAGS}
                mode="conditional"
              >
                <button
                  onClick={showFreeSpinsModal}
                  disabled={!canEditWallet}
                  className={`${!canEditWallet ? "disabled" : ""}`}
                >
                  {AppStrings.FREE_SPINS}
                </button>
              </FeatureFlagGuard>

              <FeatureFlagGuard
                mode="conditional"
                innerFeatureFlags={SPORTSBOOK_PROVIDERS_INNER_FLAGS}
              >
                <button
                  onClick={showProfitBoostModal}
                  disabled={!canEditWallet}
                  className={`${!canEditWallet ? "disabled" : ""}`}
                >
                  {AppStrings.PROFIT_BOOST}
                </button>
              </FeatureFlagGuard>
            </div>

            <div className="action-buttons">
              <div
                className="top more-actions-toggle"
                onClick={() => setIsMoreActionsExpanded((prev) => !prev)}
              >
                <p>{AppStrings.MORE_ACTIONS}</p>
                {isMoreActionsExpanded ? (
                  <ChevronUp className="chevron-icon" size={24} />
                ) : (
                  <ChevronDown className="chevron-icon" size={24} />
                )}
              </div>
              <div
                className={`more-actions-content ${isMoreActionsExpanded ? "expanded" : ""}`}
              >
                <button
                  onClick={showPersonalLimitsModal}
                  disabled={!canAddLimits}
                  className={`${!canAddLimits ? "disabled" : ""}`}
                >
                  {AppStrings.PERSONAL_LIMITS}
                </button>
                {hasSportsbookProvider && (
                  <button
                    onClick={showSportsbookVIPModal}
                    disabled={!canEditWallet}
                    className={`${!canEditWallet ? "disabled" : ""}`}
                  >
                    <img
                      src={SportsbookIcon}
                      alt="sportsbook icon"
                      className="action-button-icon"
                    />
                    {AppStrings.SPORTSBOOK_TOGGLES}
                  </button>
                )}
                {isGamePassEnabled && (
                  <button
                    onClick={showGamePassModal}
                    disabled={!canEditWallet}
                    className={`${!canEditWallet ? "disabled" : ""}`}
                  >
                    {AppStrings.GAME_PASS_TOGGLE}
                  </button>
                )}
                <button
                  onClick={showGlobalChatModal}
                  disabled={!canEditWallet}
                  className={`${!canEditWallet ? "disabled" : ""}`}
                >
                  {AppStrings.GLOBAL_CHAT_TOGGLE}
                </button>
                <button
                  onClick={showAppStoreWrittenReviewModal}
                  disabled={!canEditWallet}
                  className={`${!canEditWallet ? "disabled" : ""}`}
                >
                  {AppStrings.APP_STORE_WRITTEN_REVIEW}
                </button>
                <button
                  onClick={showVerificationEmailModal}
                  disabled={!canEditWallet}
                  className={`${!canEditWallet ? "disabled" : ""}`}
                >
                  {AppStrings.SEND_VERIFICATION_EMAIL}
                </button>
                {isSeonTriggered2faEnabled && (
                  <button
                    onClick={showSeonCooldownModal}
                    disabled={!canEditWallet}
                    className={`${!canEditWallet ? "disabled" : ""}`}
                  >
                    {AppStrings.TWO_FA_COOLDOWN}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default UserCard;
